import React, { memo, useState, useCallback } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { IconUserPlus, IconSend } from "@tabler/icons-react";
import { AttendantSelect, DepartmentSelect } from "../common";
import "./Modals.css";

/**
 * Modal de transferência de atendimento
 */
const TransferModal = memo(({
  show,
  onHide,
  onTransfer,
  conversation,
  isTransferring = false,
}) => {
  const [selectedAttendant, setSelectedAttendant] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [note, setNote] = useState("");
  const [transferType, setTransferType] = useState("attendant"); // 'attendant' ou 'department'

  const handleTransfer = useCallback(async () => {
    if (transferType === "attendant" && !selectedAttendant) return;
    if (transferType === "department" && !selectedDepartment) return;

    await onTransfer({
      attendant_id: transferType === "attendant" ? selectedAttendant?.value : null,
      department_id: transferType === "department" ? selectedDepartment?.value : null,
      transfer_reason: note.trim(),
    });

    // Reset form
    setSelectedAttendant(null);
    setSelectedDepartment(null);
    setNote("");
    setTransferType("attendant");
    onHide();
  }, [transferType, selectedAttendant, selectedDepartment, note, onTransfer, onHide]);

  const handleClose = useCallback(() => {
    setSelectedAttendant(null);
    setSelectedDepartment(null);
    setNote("");
    setTransferType("attendant");
    onHide();
  }, [onHide]);

  const isValid =
    (transferType === "attendant" && selectedAttendant) ||
    (transferType === "department" && selectedDepartment);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="sc-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="sc-modal__title">
          <IconUserPlus size={22} />
          Transferir Atendimento
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Info do contato */}
        {conversation && (
          <div className="sc-modal__info-box">
            <span className="sc-modal__info-label">Contato:</span>
            <span className="sc-modal__info-value">
              {conversation.name || conversation.phone}
            </span>
          </div>
        )}

        {/* Tipo de transferência */}
        <Form.Group className="mb-3">
          <Form.Label>Transferir para:</Form.Label>
          <div className="sc-modal__radio-group">
            <Form.Check
              type="radio"
              id="transfer-attendant"
              label="Atendente específico"
              checked={transferType === "attendant"}
              onChange={() => setTransferType("attendant")}
            />
            <Form.Check
              type="radio"
              id="transfer-department"
              label="Departamento (fila)"
              checked={transferType === "department"}
              onChange={() => setTransferType("department")}
            />
          </div>
        </Form.Group>

        {/* Seleção de atendente ou departamento */}
        {transferType === "attendant" ? (
          <Form.Group className="mb-3">
            <Form.Label>Selecione o atendente:</Form.Label>
            <AttendantSelect
              value={selectedAttendant}
              onChange={setSelectedAttendant}
              placeholder="Buscar atendente..."
              excludeCurrentUser
            />
          </Form.Group>
        ) : (
          <Form.Group className="mb-3">
            <Form.Label>Selecione o departamento:</Form.Label>
            <DepartmentSelect
              value={selectedDepartment}
              onChange={setSelectedDepartment}
              placeholder="Buscar departamento..."
            />
          </Form.Group>
        )}

        {/* Nota de transferência */}
        <Form.Group className="mb-3">
          <Form.Label>Observação (opcional):</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Informe o motivo ou contexto da transferência..."
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isTransferring}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleTransfer}
          disabled={!isValid || isTransferring}
        >
          {isTransferring ? (
            "Transferindo..."
          ) : (
            <>
              <IconSend size={16} />
              Transferir
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

TransferModal.displayName = "TransferModal";

export default TransferModal;
